import { useEffect, useRef, useMemo } from 'react';
import type { SlideImageData } from './useSliderModal';
import { cloudinaryLoader } from '../../image/loaders/cloudinary';

/**
 * Calculate appropriate preload width based on viewport.
 * Uses device pixel ratio for retina displays, capped at 1920px.
 */
const getPreloadWidth = (): number => {
  if (typeof window === 'undefined') return 1920;
  // Use 2x device pixel ratio for retina, capped at 1920
  return Math.min(Math.round(window.innerWidth * (window.devicePixelRatio || 1)), 1920);
};

/**
 * Preloads adjacent images in a gallery for smoother navigation.
 * Uses the browser's native Image object to trigger preloading.
 *
 * @param images - Full array of images
 * @param activeIndex - Currently displayed image index
 * @param isOpen - Whether the modal is open
 * @param preloadCount - Number of images to preload in each direction (default: 1)
 */
export const usePreloadAdjacentImages = (
  images: SlideImageData[],
  activeIndex: number,
  isOpen: boolean,
  preloadCount = 1,
) => {
  const preloadedUrls = useRef<Set<string>>(new Set());

  // Calculate preload width once on mount (viewport size typically doesn't change during modal view)
  const preloadWidth = useMemo(() => getPreloadWidth(), []);

  useEffect(() => {
    if (!isOpen || images.length <= 1) return;

    // Calculate indices to preload (previous and next)
    const indicesToPreload: number[] = [];
    for (let i = 1; i <= preloadCount; i++) {
      if (activeIndex - i >= 0) indicesToPreload.push(activeIndex - i);
      if (activeIndex + i < images.length) indicesToPreload.push(activeIndex + i);
    }

    indicesToPreload.forEach((index) => {
      const image = images[index];
      if (!image) return;

      // Build the full Cloudinary URL with modal preset, using responsive width
      const url = image.media?.mediaId
        ? cloudinaryLoader({
            src: image.src || '',
            mediaId: image.media.mediaId,
            width: preloadWidth,
            preset: 'modal',
          })
        : image.src;

      if (url && !preloadedUrls.current.has(url)) {
        const img = new Image();
        img.src = url;
        preloadedUrls.current.add(url);
      }
    });
  }, [images, activeIndex, isOpen, preloadCount, preloadWidth]);

  // Clear cache when modal closes
  useEffect(() => {
    if (!isOpen) {
      preloadedUrls.current.clear();
    }
  }, [isOpen]);
};
